import React, { useState, useEffect, useRef } from 'react';
import { SeatsioSeatingChart } from '@seatsio/seatsio-react';
import { Info, AlertCircle, ShoppingCart, Loader2 } from 'lucide-react';
import './SeatsioCustomStyles.css';

/**
 * SeatSelectionWidget component for seats.io integration
 * Redesigned for Glassy/Gold Aesthetic with Explicit Confirmation Flow
 */
const SeatSelectionWidget = ({
    event,
    ticketType,
    publicKey,
    onConfirmSelection, // New prop for explicit confirmation
    maxSelectable // Optional limit
}) => {
    const [selectedSeats, setSelectedSeats] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isHolding, setIsHolding] = useState(false); // Track if Seats.io is processing holds
    const [error, setError] = useState(null);
    const [chartInstance, setChartInstance] = useState(null);
    const chartRef = useRef(null);

    // Validate props
    useEffect(() => {
        if (!event?.seatsIoEventKey) {
            setError('Event does not support assigned seating');
            setIsLoading(false);
            return;
        }

        if (!publicKey) {
            setError('Seat selection is temporarily unavailable');
            setIsLoading(false);
            return;
        }

        setError(null);
    }, [event, ticketType, publicKey]);

    // Update local state when selection changes (for UI display)
    const updateLocalSelection = (selectedObjects) => {
        setSelectedSeats(selectedObjects);
    };

    // Handle explicit confirmation
    const handleConfirm = () => {
        if (onConfirmSelection && selectedSeats.length > 0) {
            const holdToken = chartInstance?.holdToken?.token || chartInstance?.holdToken;
            onConfirmSelection(selectedSeats, holdToken);
        }
    };

    // Chart callbacks for Hold management
    const handleHoldCallsInProgress = () => {
        setIsHolding(true);
    };

    const handleHoldCallsComplete = () => {
        setIsHolding(false);
    };

    // Chart configuration matching the Gold/Glassy theme
    const chartConfig = {
        workspaceKey: publicKey,
        event: event?.seatsIoEventKey,
        region: process.env.REACT_APP_SEATSIO_REGION || 'EU',
        session: 'continue', // Enable session to hold seats
        maxSelectedObjects: maxSelectable,
        language: 'en',
        loading: '<div class="seatsio-spinner"></div>',

        // Theme Colors
        objectColor: '#8E8E93', // Neutral gray for available
        sectionColor: '#3A3A3C', // Dark gray for sections
        selectedObjectColor: '#FFD700', // Gold for selection

        pricing: ticketType?.category ? [
            { 'category': ticketType.category, 'price': ticketType.priceAmount }
        ] : undefined,
    };

    if (error) {
        return (
            <div className="seat-selection-wrapper">
                <div className="seat-selection-header">
                    <h3 className="seat-selection-title">Select Seats</h3>
                </div>
                <div className="widget-alert error">
                    <AlertCircle size={20} />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    if (!event?.seatsIoEventKey || !publicKey) {
        return (
            <div className="seat-selection-wrapper">
                <div className="seat-selection-header">
                    <h3 className="seat-selection-title">General Admission</h3>
                </div>
                <div className="widget-alert info">
                    <Info size={20} />
                    <span>No seat selection required for this event.</span>
                </div>
            </div>
        );
    }

    return (
        <div className="seat-selection-wrapper">
            <div className="seat-selection-header">
                <div className="header-info">
                    <h3 className="seat-selection-title">Select Your Seats</h3>
                    <p className="seat-selection-subtitle">
                        {ticketType?.name}
                    </p>
                </div>
            </div>

            <div className="widget-alert info" style={{ background: 'rgba(255, 255, 255, 0.05)', borderColor: 'rgba(255,255,255,0.1)', color: '#ddd' }}>
                <Info size={18} />
                <span>Select seats below. They will be held for you while you checkout.</span>
            </div>

            <div className="seatsio-chart-container">
                {isLoading && (
                    <div className="seatsio-loading-overlay">
                        <div className="seatsio-spinner" />
                        <span>Loading Seat Map...</span>
                    </div>
                )}

                <SeatsioSeatingChart
                    workspaceKey={chartConfig.workspaceKey}
                    event={chartConfig.event}
                    region={chartConfig.region}
                    session={chartConfig.session}
                    maxSelectedObjects={chartConfig.maxSelectedObjects}
                    language={chartConfig.language}
                    pricing={chartConfig.pricing}
                    onHoldCallsInProgress={handleHoldCallsInProgress}
                    onHoldCallsComplete={handleHoldCallsComplete}
                    onRenderStarted={(chart) => {
                        console.log('Seats.io chart render started');
                        setChartInstance(chart);
                        chartRef.current = chart;
                    }}
                    onChartRendered={(chart) => {
                        console.log('Seats.io chart rendered successfully');
                        setIsLoading(false);
                        setError(null);
                    }}
                    onChartRenderingFailed={(chart) => {
                        console.error('Seats.io chart rendering failed');
                        setError('Failed to load seat selection. Please try refreshing the page.');
                        setIsLoading(false);
                    }}
                    onObjectSelected={(object) => {
                        // Update local state only
                        if (chartRef.current) {
                            chartRef.current.listSelectedObjects().then(selected => {
                                updateLocalSelection(selected);
                            });
                        }
                    }}
                    onObjectDeselected={(object) => {
                        // Update local state only
                        if (chartRef.current) {
                            chartRef.current.listSelectedObjects().then(selected => {
                                updateLocalSelection(selected);
                            });
                        }
                    }}
                />
            </div>

            {/* Selection Footer with Confirmation */}
            <div className="selection-footer">
                <div className="selection-summary">
                    <div className="selected-count">
                        <span className="count-label">Selected</span>
                        <span className="count-value">{selectedSeats.length}</span>
                    </div>
                    {selectedSeats.length > 0 && (
                        <div className="selected-seats-list">
                            {selectedSeats.slice(0, 3).map((seat) => (
                                <span key={seat.id} className="seat-chip-mini">
                                    {seat.labels?.displayedLabel || seat.id}
                                </span>
                            ))}
                            {selectedSeats.length > 3 && (
                                <span className="more-seats">+{selectedSeats.length - 3} more</span>
                            )}
                        </div>
                    )}
                </div>

                <div className="selection-actions">
                    <button
                        className={`confirm-seats-btn ${isHolding ? 'loading' : ''}`}
                        disabled={isHolding || isLoading}
                        onClick={handleConfirm}
                    >
                        {isHolding ? (
                            <>
                                <Loader2 size={18} className="spin-icon" />
                                <span>Holding...</span>
                            </>
                        ) : (
                            <>
                                <ShoppingCart size={18} />
                                <span>Update Cart {selectedSeats.length > 0 ? `(${selectedSeats.length})` : ''}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            <style>{`
                .selection-footer {
                    background: rgba(20, 20, 20, 0.95);
                    border-top: 1px solid rgba(255, 215, 0, 0.15);
                    padding: 16px 24px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 20px;
                }

                .selection-summary {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .selected-count {
                    display: flex;
                    flex-direction: column;
                }

                .count-label {
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: rgba(255, 255, 255, 0.5);
                }

                .count-value {
                    font-size: 20px;
                    font-weight: 600;
                    color: #fff;
                    line-height: 1.2;
                }

                .selected-seats-list {
                    display: flex;
                    gap: 6px;
                    flex-wrap: wrap;
                }

                .seat-chip-mini {
                    background: rgba(255, 215, 0, 0.1);
                    border: 1px solid rgba(255, 215, 0, 0.2);
                    color: #FFD700;
                    padding: 4px 8px;
                    border-radius: 6px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .more-seats {
                    font-size: 12px;
                    color: rgba(255, 255, 255, 0.5);
                    align-self: center;
                }

                .confirm-seats-btn {
                    background: #FFD700;
                    color: #000;
                    border: none;
                    height: 44px;
                    padding: 0 24px;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 15px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    min-width: 160px;
                    justify-content: center;
                }

                .confirm-seats-btn:hover:not(:disabled) {
                    background: #ffdb1a;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(255, 215, 0, 0.25);
                }

                .confirm-seats-btn:active:not(:disabled) {
                    transform: translateY(0);
                }

                .confirm-seats-btn:disabled {
                    background: rgba(255, 255, 255, 0.1);
                    color: rgba(255, 255, 255, 0.3);
                    cursor: not-allowed;
                    box-shadow: none;
                }

                .confirm-seats-btn.loading {
                    opacity: 0.8;
                }

                .spin-icon {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default SeatSelectionWidget;
